import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaHospital,
  FaChartLine,
  FaShieldAlt,
  FaLeaf,
  FaChalkboardTeacher,
  FaUserMd,
  FaClock,
  FaUtensils,
  FaUsers,
  FaVideo,
  FaHandHoldingHeart,
  FaHeartbeat,
  FaStopwatch
} from 'react-icons/fa';
import { MdFlipToFront } from 'react-icons/md';
import PhysicianPartnershipForm from '../components/forms/PhysicianPartnershipForm';
import OptimizedImage from '../components/common/OptimizedImage';
import { ZoomSettle, Reveal, RevealGroup, RevealItem } from '../components/motion';
import { PageHero, SectionHeading, IconMark, InkBand, LightSheet } from '../components/premium';
import { clearGlass } from '../components/glass';

// Distinct icon per patient-experience feature — a single repeated
// checkmark made the three cards read as filler.
const patientExperienceIcons = [FaHandHoldingHeart, FaHeartbeat, FaStopwatch];

// Shared settle curve for hover lifts, matching the motion kit's EASE.
const SETTLE = 'transform 0.5s cubic-bezier(0.16,1,0.3,1)';

// White card on a light surface: ink shadow, hairline edge, a small lift.
const lightCard = {
  bg: 'white',
  borderRadius: '24px',
  boxShadow: 'card',
  border: '1px solid',
  borderColor: 'rgba(14,47,58,0.06)',
  transition: SETTLE,
  _hover: { transform: 'translateY(-3px)' },
};

// Smoked glass card on an InkBand, white text.
const inkCard = {
  borderRadius: '24px',
  color: 'white',
  sx: clearGlass,
  transition: SETTLE,
  _hover: { transform: 'translateY(-3px)' },
};

const AboutFacility = () => {
  const patientExperienceFeatures = [
    {
      title: 'Personalized Care',
      description: 'Dedicated concierge style case managers guide patients from pre-op to post-op, ensuring clarity, comfort, and confidence.'
    },
    {
      title: 'Safety & Healing',
      description: 'Highest standards of infection control with digital naturistic scenes in PACU to reduce anxiety and support recovery.'
    },
    {
      title: 'Efficiency & Comfort',
      description: 'Quick OR turnover minimizes unnecessary wait, delivering a smooth, best-in-class experience.'
    }
  ];

  const equipmentFeatures = [
    {
      title: 'Best-in-Class Technology',
      description: "In talks with the world's leading equipment manufacturers to bring the latest devices that enhance patient outcomes, boost procedure efficiency, and support physician performance."
    },
    {
      title: 'Specialty Coverage',
      description: 'Planning investment across 19 specialties to ensure each department has the tools and support for excellence.'
    },
    {
      title: 'Outcome-Driven Efficiency',
      description: 'Targeting equipment that will reduce procedure times while maximizing safety, precision, and care quality.'
    }
  ];

  const physicianExperience = [
    {
      icon: FaUserMd,
      title: 'Physician Owned Entity',
      description: 'Lucien ASC is aimed to be structured as an entity where 90% of ownership and profits is reserved for physicians. Management ownership, unlike industry practices, is capped at 10%.'
    },
    {
      icon: FaClock,
      title: 'Ease in Block Time Availability',
      description: 'The center prioritizes easing on securing operating room block time by mitigating challenges faced by care givers such as facing repeated requests, denied access, case cancellations, reduced patient volume, and delayed procedures. Eliminating inefficiencies ensures scheduling with confidence and maintaining workflow without disruption to physician experience or patient care.'
    },
    {
      icon: MdFlipToFront,
      title: 'Availability of Flip Rooms',
      description: "Larger than industry standard operating rooms, allowing efficient setup and rapid transition between cases. Therefore, supporting a physician's continuous workflow of large case volumes."
    },
    {
      icon: FaUtensils,
      title: 'Attractive Modern Work Environment',
      description: "The center will include a Surgeons lounge and an in-house chef providing physician-specific meal experiences. To enhance the atmosphere for staff and patients' families, the common area will feature a grand piano. Surgical rooms are designed with a physician-centered focus, featuring exterior views and glass walls throughout to reduce the fatigue a windowless room provides."
    },
    {
      icon: FaUsers,
      title: 'Stable Senior Clinical Staff',
      description: 'Experienced, stable senior clinical staff ensuring consistency, efficiency, and high-quality patient care in every operating room.'
    },
    {
      icon: FaVideo,
      title: 'Video Integration & Innovative Visibility',
      description: 'The center supports the pathway for advancement of physicians, measurable clinical outcomes, and elevated patient experience through an integrated surgical innovation platform. Operating rooms are connected via in-OR video integration to our innovation center, allowing physicians to demonstrate, share advanced approaches, educate peers and trainees, and drive improved clinical outcomes. We also collaborate with each surgeon to incorporate professional capture and documentation, ensuring procedures are positioned for educational, academic, and strategic visibility.'
    }
  ];

  const performanceMetrics = [
    'Maximized Performance: Through hands-on management of infection control, procedure times, OR efficiency, and fixed and variable expenses, we aim to achieve OR usage rates far above industry standards.',
    'Resilient Business Model: Built on extensive feasibility analysis, risk mitigation, and sensitivity testing against risks such as recessions and pandemics, ensuring the ASC is structured for resilience and long-term stability.',
    'Data-Driven Operations: Every experience, statistic, metric, expense, and income is continuously tracked and analyzed, enabling precise decision-making and sustaining a consistently efficient performance.'
  ];

  return (
    <Box>
      {/* Hero Section */}
      {/* Photographic hero, shared with every inner page. See
          components/premium/PageHero for why the photo is no longer
          hidden under a cyan gradient. */}
      <PageHero
        title={"About Our Facility"}
        subtitle={"State-of-the-art ambulatory surgery center designed for excellence in patient care and physician performance"}
        image="/facility/exterior.jpg"
        imagePosition="center 40%"
      />

      {/* Medical Equipment Section */}
      <Container maxW="container.xl" py={20}>
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={12} alignItems="center">
          <GridItem>
            <VStack align="start" spacing={6}>
              <Reveal>
                <HStack spacing={4}>
                  <IconMark icon={FaHospital} />
                  <Heading
                    size="xl"
                    color="ink.900"
                    fontWeight="600"
                  >
                    MEDICAL EQUIPMENT
                  </Heading>
                </HStack>
              </Reveal>

              <RevealGroup w="100%">
                <VStack align="stretch" spacing={6}>
                  {equipmentFeatures.map((feature, index) => (
                    <RevealItem key={index}>
                      <Box p={{ base: 6, md: 8 }} {...lightCard}>
                        <Heading size="sm" mb={3} color="ink.900" fontWeight="600">
                          {feature.title}
                        </Heading>
                        <Text color="ink.600" lineHeight="tall">
                          {feature.description}
                        </Text>
                      </Box>
                    </RevealItem>
                  ))}
                </VStack>
              </RevealGroup>
            </VStack>
          </GridItem>

          <GridItem>
            <ZoomSettle
              h={{ base: '360px', md: '560px' }}
              w="100%"
              borderRadius="24px"
              boxShadow="card"
            >
              <Box
                h="100%"
                w="100%"
                bgImage="url('/facility/operating-room.jpg')"
                bgSize="cover"
                bgPosition="center"
                role="img"
                aria-label="State-of-the-art operating room"
              />
            </ZoomSettle>
          </GridItem>
        </Grid>
      </Container>

      {/* Physician Experience Section */}
      <InkBand image="/facility/exterior.jpg">
        <Container maxW="container.xl">
          <VStack spacing={12} align="stretch">
            <SectionHeading
              as="h2"
              tone="dark"
              size="xl"
              mb={0}
              title="PHYSICIAN EXPERIENCE"
            />

            <RevealGroup>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={{ base: 6, lg: 8 }}>
                {physicianExperience.map((item, index) => (
                  <RevealItem key={index} h="100%">
                    <Box h="100%" p={{ base: 8, lg: 10 }} {...inkCard}>
                      <IconMark icon={item.icon} tone="dark" mb={6} />
                      <Heading size="md" mb={3} color="white" fontWeight="600" lineHeight="1.25">
                        {item.title}
                      </Heading>
                      <Text color="whiteAlpha.800" lineHeight="1.62" fontSize="sm">
                        {item.description}
                      </Text>
                    </Box>
                  </RevealItem>
                ))}
              </Grid>
            </RevealGroup>
          </VStack>
        </Container>
      </InkBand>

      {/* Patient Experience Section — editorial split, photo edge-to-edge */}
      {/* Slides up over the ink band as a white sheet; the photo is clipped
          to the sheet's rounded top corner. */}
      <LightSheet tone="white" py={0} overflow="hidden">
        <Grid
          templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
          alignItems="stretch"
          minH={{ lg: '720px' }}
        >
          {/* Photo half */}
          <GridItem
            order={{ base: 1, lg: 1 }}
            position="relative"
            minH={{ base: '340px', md: '460px', lg: 'auto' }}
          >
            <ZoomSettle position="absolute" inset={0}>
              <Box
                h="100%"
                w="100%"
                bgImage="url('/facility/patient-experience.jpg')"
                bgSize="cover"
                bgPosition="center"
                role="img"
                aria-label="Patient experience at Lake Lucien Surgery Center"
              />
            </ZoomSettle>
          </GridItem>

          {/* Content half */}
          <GridItem
            order={{ base: 2, lg: 2 }}
            display="flex"
            alignItems="center"
            px={{ base: 6, md: 10, lg: 16 }}
            py={{ base: 14, lg: 20 }}
          >
            <VStack align="start" spacing={8} maxW="640px" w="100%">
              <SectionHeading
                as="h2"
                align="left"
                size="2xl"
                mb={0}
                eyebrow="Patient Experience"
                title="Care that begins long before arrival"
              />

              <RevealGroup w="100%">
                <VStack align="stretch" spacing={7} w="100%">
                  {patientExperienceFeatures.map((feature, index) => (
                    <RevealItem key={index}>
                      <HStack align="start" spacing={5}>
                        <IconMark icon={patientExperienceIcons[index]} />
                        <VStack align="start" spacing={1.5}>
                          <Heading size="md" color="ink.900" fontWeight="600">
                            {feature.title}
                          </Heading>
                          <Text color="ink.600" lineHeight="1.62">
                            {feature.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </RevealItem>
                  ))}
                </VStack>
              </RevealGroup>
            </VStack>
          </GridItem>
        </Grid>
      </LightSheet>

      {/* Outdoor ERAS Section */}
      <Container maxW="container.xl" py={20}>
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={12} alignItems="center">
          <GridItem order={{ base: 2, lg: 1 }}>
            {/* Settles out of a slight zoom as it scrolls in, so the
                photograph reads as coming to rest rather than sitting flat. */}
            <ZoomSettle h="500px" w="100%" borderRadius="24px" boxShadow="card">
              <OptimizedImage
                src="/facility/outdoor-eras.jpg"
                alt="Outdoor ERAS Recovery Area"
                w="100%"
                h="500px"
                objectFit="cover"
              />
            </ZoomSettle>
          </GridItem>

          <GridItem order={{ base: 1, lg: 2 }}>
            <VStack align="start" spacing={6}>
              <Reveal>
                <HStack spacing={4}>
                  <IconMark icon={FaLeaf} />
                  <Heading
                    size="xl"
                    color="ink.900"
                    fontWeight="600"
                  >
                    PLANNED INNOVATION: OUTDOOR ERAS
                  </Heading>
                </HStack>
              </Reveal>

              <RevealGroup w="100%">
                <VStack align="stretch" spacing={4}>
                  <RevealItem>
                    <Box p={{ base: 6, md: 8 }} {...lightCard}>
                      <Heading size="sm" mb={3} color="ink.900" fontWeight="600">
                        Innovative Recovery Vision
                      </Heading>
                      <Text color="ink.700" lineHeight="tall">
                        Exploring an Outdoor ERAS (Enhanced Recovery After Surgery) program to improve post-anesthesia recovery, accelerate healing times, and enhance patient well-being and psychology.
                      </Text>
                    </Box>
                  </RevealItem>

                  <RevealItem>
                    <Box p={{ base: 6, md: 8 }} {...lightCard}>
                      <Heading size="sm" mb={3} color="ink.900" fontWeight="600">
                        Future-Focused Planning
                      </Heading>
                      <Text color="ink.700" lineHeight="tall">
                        Actively developing plans to integrate outdoor recovery pathways that can redefine the care experience.
                      </Text>
                    </Box>
                  </RevealItem>

                  <RevealItem>
                    <Box p={{ base: 6, md: 8 }} {...lightCard}>
                      <Heading size="sm" mb={3} color="ink.900" fontWeight="600">
                        Healing Environment Potential
                      </Heading>
                      <Text color="ink.700" lineHeight="tall">
                        With 4.5 acres of landscaped grounds, the facility is positioned to support this approach, making recovery more effective and efficient.
                      </Text>
                    </Box>
                  </RevealItem>
                </VStack>
              </RevealGroup>
            </VStack>
          </GridItem>
        </Grid>
      </Container>

      {/* Conference Room Section */}
      <LightSheet tone="mist">
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={12} alignItems="center">
            <GridItem>
              <VStack align="start" spacing={6}>
                <Reveal>
                  <HStack spacing={4}>
                    <IconMark icon={FaChalkboardTeacher} />
                    <Heading
                      size="xl"
                      color="ink.900"
                      fontWeight="600"
                    >
                      FACILITY CONFERENCE ROOM
                    </Heading>
                  </HStack>
                </Reveal>

                {/* A real list, so it arrives as one block rather than
                    cascading (a motion wrapper cannot sit inside <ul>). */}
                <Reveal w="100%">
                  <List spacing={4}>
                    <ListItem p={{ base: 5, md: 6 }} {...lightCard}>
                      <HStack align="start" spacing={4}>
                        <ListIcon as={FaCheckCircle} color="brand.500" boxSize={5} mt={1} />
                        <Box>
                          <Heading size="sm" mb={2} color="ink.900" fontWeight="600">
                            Advanced Learning Hub
                          </Heading>
                          <Text color="ink.600">
                            A state-of-the-art conference room designed for doctors to conduct sessions and earn continuing education credits.
                          </Text>
                        </Box>
                      </HStack>
                    </ListItem>

                    <ListItem p={{ base: 5, md: 6 }} {...lightCard}>
                      <HStack align="start" spacing={4}>
                        <ListIcon as={FaCheckCircle} color="starOfLife.500" boxSize={5} mt={1} />
                        <Box>
                          <Heading size="sm" mb={2} color="ink.900" fontWeight="600">
                            Collaboration & Knowledge Sharing
                          </Heading>
                          <Text color="ink.600">
                            Welcomes outside medical practitioners to deliver lectures, fostering broader professional exchange.
                          </Text>
                        </Box>
                      </HStack>
                    </ListItem>

                    <ListItem p={{ base: 5, md: 6 }} {...lightCard}>
                      <HStack align="start" spacing={4}>
                        <ListIcon as={FaCheckCircle} color="brand.500" boxSize={5} mt={1} />
                        <Box>
                          <Heading size="sm" mb={2} color="ink.900" fontWeight="600">
                            Hands-On Training
                          </Heading>
                          <Text color="ink.600">
                            Includes a built-in inactive operating room for realistic, practical learning and skill development.
                          </Text>
                        </Box>
                      </HStack>
                    </ListItem>
                  </List>
                </Reveal>
              </VStack>
            </GridItem>

            <GridItem>
              <ZoomSettle h="500px" w="100%" borderRadius="24px" boxShadow="card">
                <OptimizedImage
                  src="/facility/conference-room.jpg"
                  alt="Conference Room"
                  w="100%"
                  h="500px"
                  objectFit="cover"
                />
              </ZoomSettle>
            </GridItem>
          </Grid>
        </Container>
      </LightSheet>

      {/* Where Physicians Drive Care Section */}
      {/* The wordmark artwork carries its own slate-blue plate, so it sits
          on the ink band as a rounded badge rather than bleeding into it. */}
      <InkBand py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={{ base: 8, md: 12 }} align="stretch">
            <Reveal textAlign="center" px={{ base: 4, md: 0 }}>
              <OptimizedImage
                src="/physicians-first.jpg"
                alt="Where Physicians Drive Care"
                maxW={{ base: "280px", sm: "320px", md: "400px" }}
                mx="auto"
                w="100%"
                borderRadius="16px"
              />
            </Reveal>

            <RevealGroup>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={{ base: 6, md: 8 }}>
                {performanceMetrics.map((metric, index) => (
                  <GridItem key={index}>
                    <RevealItem h="100%">
                      <Box h="100%" p={{ base: 6, md: 8 }} {...inkCard}>
                        <IconMark
                          icon={index === 0 ? FaChartLine : index === 1 ? FaShieldAlt : FaCheckCircle}
                          tone="dark"
                          size="lg"
                          mb={5}
                        />
                        <Text color="whiteAlpha.900" lineHeight="tall" fontSize={{ base: "sm", md: "md" }}>
                          {metric}
                        </Text>
                      </Box>
                    </RevealItem>
                  </GridItem>
                ))}
              </Grid>
            </RevealGroup>
          </VStack>
        </Container>
      </InkBand>

      {/* Physician Partnership Form Section */}
      <Container maxW="container.xl" py={20}>
        <Reveal>
          <PhysicianPartnershipForm />
        </Reveal>
      </Container>

      {/* CTA Section */}
      <Box bg="mist.50" py={20}>
        <Container maxW="container.xl">
          <Reveal
            bg="brand.900"
            borderRadius="24px"
            boxShadow="card"
            p={{ base: 8, md: 12 }}
            textAlign="center"
          >
            <VStack spacing={6}>
              <Heading size="xl" color="white" fontWeight="600">
                Experience the Difference
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.900" maxW="2xl">
                Schedule a facility tour or consultation to see our commitment to excellence firsthand
              </Text>
              <HStack spacing={4} pt={4}>
                <Box
                  as="a"
                  href="/contact"
                  px={8}
                  py={4}
                  bg="white"
                  color="brand.900"
                  borderRadius="pill"
                  fontWeight="600"
                  fontSize="lg"
                  cursor="pointer"
                  boxShadow="0 10px 30px -14px rgba(14,47,58,0.55)"
                  transition={SETTLE}
                  _hover={{
                    transform: 'translateY(-2px)',
                  }}
                >
                  Schedule Tour
                </Box>
              </HStack>
            </VStack>
          </Reveal>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutFacility;
